import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../../../../Services/api.service';
import { WorkflowModel, ConditionPropertyDefinition } from 'src/app/shared/workflow/workflow.models';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-editcitationworkflows',
    templateUrl: './editcitationworkflows.component.html',
    styleUrls: ['./editcitationworkflows.component.scss'],
    standalone: false
})
export class EditCitationWorkflowsComponent implements OnInit {
  public workflow: WorkflowModel | null = null;
  public pendingWorkflow: WorkflowModel | null = null;
  public isLoading = false;
  public isSaving = false;
  public hasChanges = false;

  // Citation-specific properties available for workflow conditions
  public citationConditionProperties: ConditionPropertyDefinition[] = [
    // Payment status
    { value: 'IsPaid', label: 'Is Paid', type: 'boolean' },
    { value: 'IsPartiallyPaid', label: 'Is Partially Paid', type: 'boolean' },
    { value: 'TotalPaid', label: 'Total Paid', type: 'number' },
    { value: 'TotalOutstanding', label: 'Total Outstanding', type: 'number' },
    { value: 'CurrentFee', label: 'Current Fee', type: 'number' },
    
    // Void status
    { value: 'IsVoided', label: 'Is Voided', type: 'boolean' },
    { value: 'VoidReasonId', label: 'Has Void Reason', type: 'number' },
    
    // Freeze status
    { value: 'IsFrozen', label: 'Is Frozen', type: 'boolean' },
    { value: 'IsFrozenIndefinitely', label: 'Is Frozen Indefinitely', type: 'boolean' },
    
    // Warning
    { value: 'IsWarning', label: 'Is Warning', type: 'boolean' },
    
    // Appeals
    { value: 'HasActiveAppeal', label: 'Has Active Appeal', type: 'boolean' },
    
    // Timing
    { value: 'DaysSinceIssued', label: 'Days Since Issued', type: 'number' },
    { value: 'DaysInCurrentState', label: 'Days In Current State', type: 'number' },
    
    // Escalation
    { value: 'EscalationLevel', label: 'Escalation Level', type: 'number' },
    { value: 'HasEscalated', label: 'Has Escalated', type: 'boolean' }
  ];

  constructor(private api: ApiService, private toastr: ToastrService) {}

  async ngOnInit(): Promise<void> {
    await this.loadWorkflow();
  }

  async loadWorkflow(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await this.api.Get<any>('workflows/default/Citation');
      this.workflow = this.mapResponse(response);
      this.pendingWorkflow = this.cloneWorkflow(this.workflow);
      this.hasChanges = false;
    } catch (error) {
      console.error('Failed to load citation workflow', error);
      this.toastr.error('Failed to load citation workflow.');
    } finally {
      this.isLoading = false;
    }
  }

  onWorkflowChange(model: WorkflowModel): void {
    this.pendingWorkflow = this.cloneWorkflow(model);
    this.hasChanges = true;
  }

  async save(): Promise<void> {
    if (!this.pendingWorkflow) {
      return;
    }

    this.isSaving = true;
    try {
      const request = this.buildEditRequest(this.pendingWorkflow);
      await this.api.Put('workflows', request);
      this.toastr.success('Citation workflow updated.');
      await this.loadWorkflow();
    } catch (error) {
      console.error('Failed to update workflow', error);
      this.toastr.error('Failed to update citation workflow.');
    } finally {
      this.isSaving = false;
    }
  }

  private mapResponse(response: any): WorkflowModel {
    const states = (response?.States || []).map((state: any) => ({
      Id: state.Id ?? null,
      StateKey: state.StateKey,
      Name: state.Name,
      Description: state.Description,
      IsInitial: !!state.IsInitial,
      IsFinal: !!state.IsFinal,
      IsActive: !!state.IsActive
    }));

    const transitions = (response?.Transitions || []).map((transition: any) => ({
      Id: transition.Id ?? null,
      FromStateKey: transition.FromStateKey,
      ToStateKey: transition.ToStateKey,
      Name: transition.Name,
      Description: transition.Description,
      IsManualTrigger: !!transition.IsManualTrigger,
      IsAutomaticTrigger: !!transition.IsAutomaticTrigger,
      MinimumTimeInSourceState: transition.MinimumTimeInSourceState ?? 0,
      Priority: transition.Priority ?? 1,
      Conditions: (transition.Conditions || transition.ConditionEntities || []).map((c: any) => ({
        Id: c.Id ?? null,
        PropertyName: c.PropertyName,
        Operator: c.Operator,
        Value: c.Value,
        ExecutionOrder: c.ExecutionOrder ?? 1
      }))
    }));

    return {
      Id: response?.Id ?? 0,
      TargetType: response?.TargetType ?? 'Citation',
      Name: response?.Name ?? 'Citation Workflow',
      Description: response?.Description,
      IsActive: response?.IsActive ?? true,
      IsDefault: response?.IsDefault ?? true,
      States: states,
      Transitions: transitions
    };
  }

  private buildEditRequest(workflow: WorkflowModel): any {
    return {
      Id: workflow.Id,
      Name: workflow.Name,
      Description: workflow.Description,
      IsActive: workflow.IsActive,
      IsDefault: workflow.IsDefault,
      DesignerProperties: null,
      States: workflow.States.map(state => ({
        Id: state.Id ?? undefined,
        StateKey: state.StateKey,
        Name: state.Name,
        Description: state.Description,
        IsInitial: state.IsInitial,
        IsFinal: state.IsFinal,
        IsActive: state.IsActive,
        DesignerProperties: null,
        Actions: []
      })),
      Transitions: workflow.Transitions.map(transition => ({
        Id: transition.Id ?? undefined,
        FromStateKey: transition.FromStateKey,
        ToStateKey: transition.ToStateKey,
        Name: transition.Name,
        Description: transition.Description,
        IsManualTrigger: transition.IsManualTrigger,
        IsAutomaticTrigger: transition.IsAutomaticTrigger,
        MinimumTimeInSourceState: transition.MinimumTimeInSourceState ?? 0,
        DesignerProperties: null,
        Priority: transition.Priority ?? 1,
        Actions: [],
        Conditions: (transition.Conditions || []).map(c => ({
          Id: c.Id ?? undefined,
          PropertyName: c.PropertyName,
          Operator: c.Operator,
          Value: c.Value,
          ExecutionOrder: c.ExecutionOrder ?? 1
        }))
      }))
    };
  }

  private cloneWorkflow(model: WorkflowModel): WorkflowModel {
    return {
      Id: model.Id,
      TargetType: model.TargetType,
      Name: model.Name,
      Description: model.Description,
      IsActive: model.IsActive,
      IsDefault: model.IsDefault,
      States: model.States.map(state => ({ ...state })),
      Transitions: model.Transitions.map(transition => ({ 
        ...transition,
        Conditions: transition.Conditions?.map(c => ({ ...c })) || []
      }))
    };
  }
}
